import { useState, useContext } from 'react';
import {
  Button,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CreateIcon from '@mui/icons-material/Create';
import useGlynt from '../../hooks/useGlynt';
import { AppContext } from '../../../App';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTrainingSetUpdate } from '../../hooks/useTrainingSetUpdate';
import EventNoteIcon from '@mui/icons-material/EventNote';

interface ITrainingSetDescriptionEditProps {
  trainingSetId: string;
  trainingSetDescription: string;
  iconType: 'edit' | 'note' | 'text';
  tooltip?: string;
}

function TrainingSetDescriptionEdit({
  trainingSetId,
  trainingSetDescription,
  iconType = 'edit',
  tooltip,
}: ITrainingSetDescriptionEditProps) {
  const { setToast, isStaff } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(trainingSetDescription);
  const [apiError, setApiError] = useState('');
  const client = useQueryClient();
  const theme = useTheme();

  const mutation = useTrainingSetUpdate({
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['trainingSet']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Note saved',
      });
      setOpen(false);
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: `Something went wrong. ${error.message}`,
      });
    },
  });

  const handleSubmit = () => {
    if (value !== trainingSetDescription) {
      mutation.mutate({ trainingSetId: trainingSetId, data: { description: value } });
    } else {
      setOpen(false);
    }
  };

  const title = isStaff ? 'Edit Description' : 'View Description';

  if (!isStaff && (trainingSetDescription === '' || !trainingSetDescription)) {
    return null;
  }

  return (
    <>
      {iconType === 'edit' ? (
        <Tooltip placement="top" title={tooltip}>
          <CreateIcon
            style={{
              color: theme.palette.custom.lightGreen,
              cursor: 'pointer',
              fontSize: '15px',
              marginLeft: '10px',
            }}
            data-cy="btn-ts-rename-description"
            onClick={() => {
              setOpen(true);
              setApiError('');
            }}
          />
        </Tooltip>
      ) : iconType === 'note' && trainingSetDescription ? (
        <Tooltip placement="top" title={tooltip}>
          <EventNoteIcon
            style={{
              color: theme.palette.custom.lightGreen,
              cursor: 'pointer',
              fontSize: '15px',
              marginLeft: '10px',
            }}
            data-cy="btn-ts-note-description"
            onClick={() => {
              setOpen(true);
              setApiError('');
            }}
          />
        </Tooltip>
      ) : iconType === 'note' && (!trainingSetDescription || trainingSetDescription === '') ? (
        <Tooltip placement="top" title="No description set">
          <EventNoteIcon
            style={{
              color: theme.palette.custom.lightGray,
              cursor: 'pointer',
              fontSize: '15px',
              marginLeft: '10px',
            }}
            data-cy="btn-ts-view-description"
            onClick={() => {
              setOpen(true);
              setApiError('');
            }}
          />
        </Tooltip>
      ) : iconType === 'text' ? (
        trainingSetDescription ? (
          <Tooltip placement="bottom" title={trainingSetDescription}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#1565c0',
                backgroundColor: 'rgba(21, 101, 192, 0.08)',
                borderRadius: '4px',
                px: 1,
                py: 0.25,
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '330px',
                '&:hover': {
                  backgroundColor: 'rgba(21, 101, 192, 0.15)',
                },
              }}
              data-cy="btn-ts-text-description"
              onClick={() => {
                setOpen(true);
                setApiError('');
              }}
            >
              {trainingSetDescription}
            </Typography>
          </Tooltip>
        ) : (
          <Typography
            sx={{
              fontSize: '12px',
              color: theme.palette.custom.lightGray,
              fontStyle: 'italic',
              cursor: isStaff ? 'pointer' : 'default',
            }}
            data-cy="btn-ts-no-description"
            onClick={
              isStaff
                ? () => {
                    setOpen(true);
                    setApiError('');
                  }
                : undefined
            }
          >
            No description set
          </Typography>
        )
      ) : null}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="dialog-field"
        PaperProps={{ style: { width: '600px' } }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            placeholder={title}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            inputProps={{ maxLength: 255 }}
            fullWidth
            data-cy="input-ts-description"
            disabled={!isStaff}
          />
          {apiError.length > 0 && <DialogContentText color="error">{apiError}</DialogContentText>}
        </DialogContent>
        <DialogActions>
          <Button
            id="cancel"
            data-cy="btn-dialog-field-cancel"
            onClick={() => {
              setOpen(false);
              setApiError('');
            }}
          >
            Cancel
          </Button>
          {isStaff ? (
            <Button
              id="submit"
              onClick={handleSubmit}
              color="primary"
              disabled={value === trainingSetDescription}
              data-cy="btn-dialog-field-submit"
            >
              Submit
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TrainingSetDescriptionEdit;
